import { createClient } from '@supabase/supabase-js';

import { env } from './env';

export const supabase = createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/**
 * Absolute URL an auth email or OAuth hand-off should come back to.
 *
 * Built from `window.location.origin`, NOT from `env.APP_BASE_URL`. Those are
 * different things and conflating them is a real bug: `VITE_APP_BASE_URL` is
 * the app's *canonical* address (env.template calls it "this app's own
 * canonical URL. Used to build share URLs"), so in any local `.env.local`
 * pointing at production a password-recovery mail sent from localhost arrives
 * with a production link — you click it and land on the deployed site instead
 * of the app you were working in. Share URLs still want the canonical value;
 * auth round-trips want wherever the user actually is.
 *
 * Caveat that no amount of client code can fix: Supabase only honours
 * `redirectTo` when the URL matches the project's Redirect URLs allow-list.
 * Anything else silently falls back to the configured Site URL — same
 * production-link symptom, different cause. Each dev origin has to be added
 * there once (e.g. `http://localhost:5186/**`).
 */
export function authRedirectUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

export async function signInWithGoogle(redirectTo?: string): Promise<void> {
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectTo ?? authRedirectUrl('/auth/callback'),
    },
  });
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}
